import { AsyncPipe, NgClass, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import { Appointment, Fitter, GeoCoordinates } from '@sc/data-access-models';
import { DossierSandboxService } from '@sc/data-access-state-management';
import { LocaleDatePipe } from '@sc/data-access-translation';
import { MomentParseZonePipe } from '@sc/ui-components';
import { BaseGMapOverlay } from '../../../../utils/base-gmap-overlay';

@Component({
  selector: 'sc-gmap-appointment-time',
  styleUrls: ['./gmap-appointment-time.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    AsyncPipe,
    TitleCasePipe,
    MomentParseZonePipe,
    TranslateModule,
    LocaleDatePipe,
  ],
  template: `
    <div
      class="map-element"
      [ngClass]="{
        'outside-appointment': appointment.outsideIntervention,
        'center-appointment': !appointment.outsideIntervention,
        'has-custom-dot-icon': hasCustomDotImage()
      }"
    >
      <div
        class="map-element-container"
        [ngClass]="{
          hover:
            fitterHover === appointment.fitterId &&
            !appointment.outsideIntervention
        }"
        tabindex="0"
        role="button"
        [attr.aria-label]="
          'GlobalApplicativeConstants.accessibility.labels.mapAppointment'
            | translate
              : {
                  fitterName: fitter?.brand,
                  apmtDate:
                    appointment.time
                    | parseZoneTimeString
                      : timezoneService.getTimezone(appointment)
                    | localeDate: 'fullDate'
                    | async,
                  apmtTime:
                    appointment.time
                    | parseZoneTimeString
                      : timezoneService.getTimezone(appointment)
                    | localeDate: 'shortTime'
                    | async,
                  isOutside: appointment.outsideIntervention
                }
        "
        (click)="onClick($event)"
        (keydown.enter)="onClick($event)"
        (keydown.space)="onClick($event)"
      >
        <div class="day-info sc-design-text-4-bold" aria-hidden="true">
          {{
            appointment.time
              | parseZoneTimeString: timezoneService.getTimezone(appointment)
              | localeDate: 'EEEE'
              | async
              | titlecase
          }}
        </div>
        <div class="date-info sc-design-text-4-bold" aria-hidden="true">
          {{
            appointment.time
              | parseZoneTimeString: timezoneService.getTimezone(appointment)
              | localeDate: 'shortDate'
              | async
          }}
        </div>
        <div class="hour-info sc-design-text-4" aria-hidden="true">
          {{ dossierSB.getTime(appointment) }}
        </div>
      </div>
    </div>
  `,
})
export class GmapAppointmentTimeComponent
  extends BaseGMapOverlay
  implements OnChanges, OnDestroy
{
  @Input() public map: google.maps.Map;
  @Input() public appointment: Appointment;
  @Input() public fitter: Fitter;
  @Input() public fitterHover: string;
  @Input() public set location(newLoc: GeoCoordinates) {
    this.position = newLoc;
  }

  @Output() public clickEvent = new EventEmitter<Appointment>();

  constructor(
    public timezoneService: TimezoneService,
    elRef: ElementRef,
    private readonly configService: ConfigurationService,
    public dossierSB: DossierSandboxService
  ) {
    super(elRef, 'gmap-appointment-time');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.map?.previousValue && !!changes.map?.currentValue) {
      this.gmap = changes.map.currentValue;
    }

    if (
      changes.fitterHover?.previousValue !== changes.fitterHover?.currentValue
    ) {
      this.updateClass(
        this.fitterHover === this.appointment.fitterId ? 'hover' : '',
        this.appointment?.outsideIntervention
          ? 'outside-appointment-overlay'
          : ''
      );
    }
  }

  ngOnDestroy(): void {
    this.onRemove();
  }

  hasCustomDotImage() {
    return !!this.configService.getConfig()?.map?.dotIcon;
  }

  onClick(clickEvent: Event) {
    this.clickEvent.emit(this.appointment);
    clickEvent.stopPropagation();
  }
}
